function isPlanQuotaExhausted(plan) {
    if (!plan || typeof plan !== "object") return false;
    const quotaStatus = plan.quota_status ?? plan.quotaStatus;
    if (quotaStatus === "exhausted") return true;
    if (quotaStatus === "available") return false;
    const campaignRemaining = plan.campaign?.remaining;
    const postRemaining = plan.post?.remaining;
    if (campaignRemaining === 0 && postRemaining === 0) return true;
    return false;
}

function getPlanPeriodStart(plan) {
    return plan?.period_start ?? plan?.periodStart ?? plan?.plan_period_start ?? null;
}

export function resolveActivePlan(plans) {
    if (!Array.isArray(plans) || plans.length === 0) return null;

    const availablePlans = plans.filter((plan) => !isPlanQuotaExhausted(plan));
    if (availablePlans.length === 0) return null;

    return [...availablePlans].sort((a, b) => {
        const aStart = getPlanPeriodStart(a);
        const bStart = getPlanPeriodStart(b);
        if (!aStart && !bStart) return 0;
        if (!aStart) return 1;
        if (!bStart) return -1;
        return new Date(bStart).getTime() - new Date(aStart).getTime();
    })[0];
}

export function getDisplayPlanName(planData, { loading = false, loaded = false } = {}) {
    if (loading) return "Loading…";
    if (loaded && !planData?.hasPlans) return "No plans";
    return planData?.activePlan?.name ?? "Free Plan";
}

export function normalizePlanSummary(raw) {
    if (!raw) return null;

    const plans = Array.isArray(raw.plans) ? raw.plans : [];

    return {
        campaign: raw.campaign ?? { limit: 0, used: 0, remaining: 0 },
        post: raw.post ?? { limit: 0, used: 0, remaining: 0 },
        plans,
        activePlan: resolveActivePlan(plans),
        hasPlans: plans.length > 0,
    };
}

export function getSubscribedPlanIds(planData) {
    if (!Array.isArray(planData?.plans)) return new Set();
    return new Set(
        planData.plans
            .map((plan) => plan.plan_id ?? plan.planId)
            .filter(Boolean)
    );
}

export function isSubscribedPlan(planData, planId) {
    if (!planId) return false;
    return getSubscribedPlanIds(planData).has(planId);
}

export function formatUsage({ used = 0, limit = 0 } = {}) {
    return `${used}/${limit}`;
}

export function isCampaignQuotaExhausted(planData) {
    return Boolean(planData?.hasPlans) && planData.campaign?.remaining === 0;
}

function normalizeCatalogPlan(plan) {
    if (!plan || typeof plan !== "object") return null;
    if (plan.is_active === false || plan.isActive === false) return null;

    const planId = plan.plan_id ?? plan.planId ?? plan.id ?? "";
    const price = plan.price ?? null;

    return {
        plan_id: planId,
        name: plan.name ?? "Plan",
        description: plan.description ?? "",
        stripe_price_id: plan.stripe_price_id ?? price?.price_id ?? "",
        campaign_quota: Number(plan.campaign_quota ?? plan.campaignQuota ?? 0),
        post_quota: Number(plan.post_quota ?? plan.postQuota ?? 0),
        duration_days: Number(plan.duration_days ?? plan.durationDays ?? 0),
        is_active: plan.is_active ?? plan.isActive ?? true,
        price: price
            ? {
                  price_id: price.price_id ?? "",
                  product_id: price.product_id ?? "",
                  type: price.type ?? "",
                  currency: price.currency ?? "inr",
                  unit_amount: Number(price.unit_amount ?? 0),
                  active: price.active !== false,
                  recurring: price.recurring ?? null,
                  metadata: price.metadata ?? {},
              }
            : null,
    };
}

export function normalizePlansCatalog(raw) {
    if (!raw) return [];

    const list =
        raw.plans ??
        raw.data?.plans ??
        (Array.isArray(raw) ? raw : []);

    return list
        .map(normalizeCatalogPlan)
        .filter(Boolean)
        .sort((a, b) => a.duration_days - b.duration_days);
}

export function formatPlanDuration(days) {
    const value = Number(days);
    if (value === 1) return "1 day";
    return `${value} days`;
}
